import * as React from 'react';

import { BarItemIdentifier, chartsAxisHighlightClasses } from '@mui/x-charts';
import { barElementClasses, BarPlot } from '@mui/x-charts/BarChart';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';

import { ItemTooltipTopElement } from './ItemTooltipTopElement';

interface DashboardBarChartProps {
  months: string[];
  barHeight: number[];
  ezshipprocessed: number;
  ezshipunprocessed: number;
  onBarItemClick: (d: BarItemIdentifier) => void;
}

export default function BarChartWithTopFixedTooltip({
  months,
  barHeight,
  ezshipprocessed,
  ezshipunprocessed,
  onBarItemClick,
}: DashboardBarChartProps) {
  const id = React.useId();

  const clipPathId = `${id}-clip-path`;

  const pData = [ezshipprocessed, ...new Array(Math.max(0, (barHeight?.length || 0) - 1)).fill(0)];

  const totalForMay = ezshipprocessed + ezshipunprocessed;
  const maxYValue = Math.max(totalForMay, ...barHeight);

  return (
    <div className="">
      <ResponsiveChartContainer
        height={267}
        series={[
          {
            type: 'bar',
            data: pData,
            stack: 'total',
            label: '',
            color: '#154734',
            id: 'pvId',
            valueFormatter: (value: number | null) => {
              return `$${value} PV`;
            },
          },
          {
            type: 'bar',
            data: [ezshipunprocessed, ...barHeight],
            stack: 'total',
            label: '',
            id: 'uvId',
            valueFormatter: (value: number | null) => {
              return `$${value} PV`;
            },
            color: '#E08D6E',
          },
        ]}
        xAxis={[
          {
            data: months,
            scaleType: 'band',
            disableLine: true,
            disableTicks: true,
            // @ts-ignore
            showGrid: true,
            tickLabelStyle: {
              fontFamily: 'var(--font-sweet-sans-pro)',
              fontSize: 12,
              fill: 'black',
              fontWeight: 400,
            },
          },
        ]}
        yAxis={[
          {
            // @ts-ignore
            axisId: 'yAxis1',
            scaleType: 'linear',
            min: 0,
            max: Math.ceil(maxYValue / 1000) * 1000, // round up to nearest 1000 for padding
            valueFormatter: (value: number) => {
              if (value) {
                return `${(value / 1000).toFixed(0)}k`;
              }
              return '0';
            },
            disableLine: true,
            disableTicks: true,
            tickLabelStyle: {
              fontFamily: 'var(--font-sweet-sans-pro)',
              fontSize: 12,
              fill: 'black',
              fontWeight: 400,
            },
          },
        ]}
        sx={{
          [`& .${barElementClasses.root}[data-series-id="pvId"]`]: {
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
          },
          [`& .${barElementClasses.root}[data-series-id="uvId"]`]: {
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          },
          [`& .${barElementClasses.root}[data-series-id="pvId"]`]: {
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            '&:hover': {
              boxShadow: 'none',
            },
          },
          [`& .${barElementClasses.root}[data-series-id="uvId"]`]: {
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            '&:hover': {
              boxShadow: 'none',
            },
          },
          [`& .${chartsAxisHighlightClasses.root}`]: {
            fill: 'white',
          },
          '& .MuiChartsGrid-line': {
            stroke: '#D0D5DD',
            strokeWidth: 1,
          },
        }}
      >
        <ChartsGrid horizontal />
        <g clipPath={`url(#${clipPathId})`}>
          <BarPlot onItemClick={(event, d) => onBarItemClick(d)} borderRadius={4} />
        </g>
        <ChartsXAxis />
        <ChartsYAxis />
        <ItemTooltipTopElement pvData={pData} uvData={[ezshipunprocessed, ...barHeight]} />
        <ChartsClipPath id={clipPathId} />
      </ResponsiveChartContainer>
    </div>
  );
}
